import { useCallback, useRef } from 'react';
import { LevelData, Difficulty } from '../types';

export const useDDA = () => {
  const successStreak = useRef(0);
  
  const updatePerformance = useCallback((levelData: LevelData): Difficulty => {
    // Simple scoring: if accuracy > 80% and completed within reasonable time
    const expectedTime = levelData.gridSize * levelData.gridSize * 3; // 3 seconds per cell (more forgiving)
    const didWellThisLevel = levelData.accuracyPct >= 80 && levelData.completionTimeSec <= expectedTime;
    
    if (didWellThisLevel) {
      successStreak.current += 1;
    } else {
      successStreak.current = 0;
    }
    
    // Current difficulty
    const currentDifficulty = {
      gridSize: levelData.gridSize,
      obstaclesCount: levelData.obstaclesCount,
      showHints: true // Always show hints for now
    };
    
    // Only increase difficulty after 2 successful levels in a row
    if (successStreak.current >= 2) {
      successStreak.current = 0; // Reset streak after increasing difficulty
      
      // Increase grid size first, then add obstacles
      if (currentDifficulty.gridSize < 5) { // Max 5x5 for simpler game
        return {
          gridSize: currentDifficulty.gridSize + 1,
          obstaclesCount: currentDifficulty.obstaclesCount,
          showHints: true
        };
      } else if (currentDifficulty.obstaclesCount < 3) { // Max 3 obstacles
        return {
          gridSize: currentDifficulty.gridSize,
          obstaclesCount: currentDifficulty.obstaclesCount + 1,
          showHints: true
        };
      }
    }
    
    // If player is struggling (accuracy < 50%), make it easier
    if (levelData.accuracyPct < 50) {
      return {
        gridSize: Math.max(3, currentDifficulty.gridSize - 1),
        obstaclesCount: Math.max(0, currentDifficulty.obstaclesCount - 1),
        showHints: true
      };
    }
    
    // Otherwise keep current difficulty
    return currentDifficulty;
  }, []);

  const reset = useCallback(() => {
    successStreak.current = 0;
  }, []);

  return {
    updatePerformance,
    reset
  };
};